import type { AdminProfile } from '@fonus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PauseCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './components/ui/button.tsx';
import { Skeleton } from './components/ui/skeleton.tsx';
import { api, tokenStore } from './lib/api.ts';
import { useReadiness } from './lib/queries.ts';
import { go, useRoute } from './lib/router.ts';
import { cn } from './lib/utils.ts';
import { Home } from './pages/Home.tsx';
import { Journal } from './pages/Journal.tsx';
import { Login } from './pages/Login.tsx';
import { NewStory } from './pages/NewStory.tsx';
import { Settings } from './pages/Settings.tsx';
import { Stories } from './pages/Stories.tsx';
import { StoryPage } from './pages/StoryPage.tsx';
import { Voices } from './pages/Voices.tsx';

const NAV = [
  ['', 'Главная'],
  ['stories', 'Сказки'],
  ['voices', 'Голоса'],
  ['journal', 'Журнал'],
  ['settings', 'Настройки'],
] as const;

function PauseBanner({ isOwner }: { isOwner: boolean }) {
  const r = useReadiness();
  const qc = useQueryClient();
  const resume = useMutation({
    mutationFn: () => api<{ pausedByEnv: boolean }>('/admin/content/resume', { method: 'POST' }),
    onSuccess: (x) => {
      toast[x.pausedByEnv ? 'warning' : 'success'](
        x.pausedByEnv
          ? 'Пауза в админке снята, но в Railway включена CONTENT_PAUSED'
          : 'Генерация снова доступна',
      );
      void qc.invalidateQueries({ queryKey: ['readiness'] });
    },
  });
  if (!r.data?.paused) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-warn/30 bg-warn/[.09] px-4 py-2.5 text-[14px]">
      <PauseCircle className="size-5 text-warn" />
      <span className="flex-1">
        <b>Генерация и озвучка остановлены.</b>{' '}
        {r.data.pausedByEnv
          ? 'Пауза включена в Railway (CONTENT_PAUSED) — снять её можно только там.'
          : 'Новые задачи не запускаются.'}
      </span>
      {isOwner && !r.data.pausedByEnv && (
        <Button size="sm" disabled={resume.isPending} onClick={() => resume.mutate()}>
          Включить
        </Button>
      )}
    </div>
  );
}

export function App() {
  const [token, setToken] = useState(tokenStore.get());
  useEffect(() => {
    const on = () => setToken(tokenStore.get());
    window.addEventListener('fonus-admin-auth', on);
    return () => window.removeEventListener('fonus-admin-auth', on);
  }, []);
  const me = useQuery({
    queryKey: ['me', token],
    queryFn: () => api<AdminProfile>('/admin/me'),
    enabled: token !== null,
    retry: false,
  });
  const route = useRoute();

  if (!token) return <Login />;
  if (me.isLoading)
    return (
      <div className="mx-auto mt-10 max-w-5xl px-4">
        <Skeleton className="h-10 w-64" />
      </div>
    );
  if (!me.data) return <Login />;
  const role = me.data.role;
  const canEdit = role !== 'VIEWER';
  const isOwner = role === 'OWNER';
  const [page = '', id] = route;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-line bg-card/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-1 px-4 py-2.5">
          <button
            type="button"
            className="mr-3 flex items-center gap-2 font-bold"
            onClick={() => go('')}
          >
            <span className="flex size-7 items-center justify-center rounded-[9px] bg-accent text-[14px] text-white">
              🌙
            </span>{' '}
            Fonus Ertak
          </button>
          <nav aria-label="Главное меню" className="flex flex-wrap gap-0.5">
            {NAV.map(([path, label]) => {
              const active = page === path;
              return (
                <a
                  key={path}
                  href={`#/${path}`}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-[15px] font-medium transition',
                    active ? 'text-accent' : 'text-muted hover:text-fg',
                  )}
                >
                  {label}
                </a>
              );
            })}
          </nav>
          <span className="flex-1" />
          <span className="text-[13px] text-muted">
            {me.data.name} · {isOwner ? 'владелец' : role === 'EDITOR' ? 'редактор' : 'просмотр'}
          </span>
          <Button variant="ghost" size="sm" onClick={() => tokenStore.set(null)}>
            Выйти
          </Button>
        </div>
        <PauseBanner isOwner={isOwner} />
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">
        {page === '' && <Home isOwner={isOwner} />}
        {page === 'stories' &&
          id === 'new' &&
          (canEdit ? (
            <NewStory />
          ) : (
            <p className="text-muted">Создавать сказки может редактор или владелец.</p>
          ))}
        {page === 'stories' && id && id !== 'new' && (
          <StoryPage id={id} canEdit={canEdit} isOwner={isOwner} />
        )}
        {page === 'stories' && !id && <Stories canEdit={canEdit} isOwner={isOwner} />}
        {page === 'voices' && <Voices isOwner={isOwner} />}
        {page === 'journal' && <Journal canEdit={canEdit} isOwner={isOwner} />}
        {page === 'settings' && <Settings isOwner={isOwner} />}
      </main>
    </div>
  );
}
